import React, { useState } from 'react';
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from 'react-toastify';
import { ResetPassword } from '../../../services/backendServices';
import { validatePassword } from '../../../Util/validation';
import { WithTooltip } from '../../user-pages/ui-helper';
import DraggableModalWrapper from '../reusable/DraggableModalWrapper';
/** 
 * ResetpasswordModal Component
 * --------------------------------
 * This component is a modal for resetting the password.
 * It includes fields for entering the old password, new password, and confirm password.
 * Props:
 * @param {boolean} show - Flag to control the visibility of the modal.
 * 	* @param {function} onClose - Function to close the modal.
 */
function ResetpasswordModal({ show, onClose }) {
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [oldPassword, setOldPassword] = useState("");
	const [showOldPassword, setShowOldPassword] = useState(false); // Toggle old password visibility
	const [showPassword, setShowPassword] = useState(false); // Toggle new password visibility
	const [showConfirmPassword, setShowConfirmPassword] = useState(false); // Toggle confirm password visibility
	const [isLoading, setIsLoading] = useState(false); // Track API call status

	// Handle Reset Password Submit
	const handleResetPasswordSubmit = async (event) => {
		event.preventDefault();
		if (isLoading) return;
		const oldPass = oldPassword.trim();
		const newPass = newPassword.trim();
		const confirmPass = confirmPassword.trim();
		if (!oldPass) {
			toast.error("Please enter your old password.");
			return;
		}
		if (!validatePassword(newPass)) {
			toast.error("Password must be at least 6 characters long.");
			return;
		}

		if (newPass !== confirmPass) {
			toast.error("New password and confirm password do not match.");
			return;
		}
		if (oldPass === newPass) {
			toast.error("New password must be different from the old password.");
			return;
		}
		const payload = {
			old_password: oldPass,
			new_password: newPass,
		}
		ResetPasswordApiCall(payload);
	};
	// API Call For Reset password
	const ResetPasswordApiCall = async (Values) => {
		setIsLoading(true);
		try {
			const res = await ResetPassword(Values);
			if (res.status) {
				toast.success(res.msg);
				onClose();
			} else {
				toast.error(res.msg);
			}
		} catch (e) {
			console.error(e);
		} finally {
			setIsLoading(false);
		}
	};
	return (
		<Modal show={show} animation={false} size="md" >
			<DraggableModalWrapper>
				<Modal.Header closeButton onClick={onClose} className="drggable-cursor me-2 close-button" >
					<Modal.Title>
						<WithTooltip tooltipText="Drag to move the modal"><i className="mdi mdi-drag info-text me-2 fs-5" ></i></WithTooltip>
						Reset Password</Modal.Title>
				</Modal.Header>
				<Modal.Body className={`p-10`}>
					<Form onSubmit={handleResetPasswordSubmit} onKeyDown={(e) => { if (e.key === 'Enter') handleResetPasswordSubmit(e); }}>
						<Form.Group className='mb-2'>
							<Form.Label>Old Password</Form.Label>
							<div className="password-wrapper">
								<Form.Control
									type={showOldPassword ? "text" : "password"}
									placeholder="Enter Old Password"
									value={oldPassword}
									onChange={(e) => setOldPassword(e.target.value)}
									disabled={isLoading}
									autoComplete="current-password"
									required
								/>
								<div onClick={() => setShowOldPassword(!showOldPassword)} className="toggle-password cursor-pointer">
									<i className={`mdi ${showOldPassword ? "mdi-eye" : "mdi-eye-off"} text-muted`} ></i>
								</div>
							</div>
						</Form.Group>
						<Form.Group className='mb-2'>
							<Form.Label>New Password</Form.Label>
							<div className="password-wrapper">
								<Form.Control
									type={showPassword ? "text" : "password"}
									placeholder="Enter New Password"
									value={newPassword}
									onChange={(e) => setNewPassword(e.target.value)}
									autoComplete="new-password"
									disabled={isLoading}
									required
								/>
								<div onClick={() => setShowPassword(!showPassword)} className="toggle-password cursor-pointer">
									<i className={`mdi ${showPassword ? "mdi-eye" : "mdi-eye-off"} text-muted`} ></i>
								</div>
							</div>
						</Form.Group>

						<Form.Group >
							<Form.Label>Confirm New Password</Form.Label>
							<div className="password-wrapper">
								<Form.Control
									type={showConfirmPassword ? "text" : "password"}
									placeholder="Confirm New Password"
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									autoComplete="new-password"
									disabled={isLoading}
									required
								/>
								<div onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="toggle-password cursor-pointer">
									<i className={`mdi ${showConfirmPassword ? "mdi-eye" : "mdi-eye-off"} text-muted`} ></i>
								</div>
							</div>
						</Form.Group>
					</Form>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="danger" onClick={onClose} disabled={isLoading}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleResetPasswordSubmit} disabled={isLoading}>
						{isLoading ? (<i className='mdi mdi-loading mdi-spin'></i>) : ("Submit")}
					</Button>
				</Modal.Footer>
			</DraggableModalWrapper>
		</Modal>
	);
}

export default ResetpasswordModal;
